import { useEffect, useRef, useState } from 'react'
import { Upload, message } from 'antd'
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import { HiOutlineDocument, HiOutlineTrash } from 'react-icons/hi'
import { Button } from '@/components/ui'
import { api } from '@/lib/apiClient'
import {
  encodedSize,
  formatFileSize,
  type AttachmentLimits,
  type EmailAttachment,
} from '../types'

type Props = {
  eventSlug: string
  value: EmailAttachment[]
  onChange: (attachments: EmailAttachment[]) => void
  limits: AttachmentLimits
  /** Size of the email body, which counts towards the same SendGrid limit. */
  bodyBytes?: number
  disabled?: boolean
}

type UploadResponse = { attachment: EmailAttachment }

/**
 * Picks files, uploads each one immediately, and keeps the signed ids the form
 * submits. Sizes are checked here against the same SendGrid limits the server
 * enforces, so an over-limit file is refused before it is uploaded.
 */
const EmailAttachmentsField = ({
  eventSlug,
  value,
  onChange,
  limits,
  bodyBytes = 0,
  disabled = false,
}: Props) => {
  const [uploadCount, setUploadCount] = useState(0)

  // Picking several files at once calls `beforeUpload` for all of them before
  // the first upload resolves, so the list and the bytes claimed by uploads
  // still in flight are tracked in refs the checks below can read right away.
  const listRef = useRef(value)
  const inFlightBytesRef = useRef(0)
  useEffect(() => {
    listRef.current = value
  }, [value])

  const encodedTotal = (list: EmailAttachment[]) =>
    list.reduce((total, file) => total + encodedSize(file.byteSize), 0)

  const applyChange = (next: EmailAttachment[]) => {
    listRef.current = next
    onChange(next)
  }

  const handleUpload = async (file: File) => {
    if (file.size > limits.maxFileBytes) {
      message.error(
        `"${file.name}" is ${formatFileSize(file.size)}, over the ${formatFileSize(
          limits.maxFileBytes
        )} limit for a single file.`
      )
      return false
    }

    const claimed = encodedSize(file.size)
    const wouldUse = bodyBytes + encodedTotal(listRef.current) + inFlightBytesRef.current + claimed
    if (wouldUse > limits.maxTotalBytes) {
      message.error(
        `Adding "${file.name}" would push this email past the ${formatFileSize(
          limits.maxTotalBytes
        )} total that SendGrid allows. Remove a file and try again.`
      )
      return false
    }

    inFlightBytesRef.current += claimed
    setUploadCount((count) => count + 1)
    try {
      const body = new FormData()
      body.append('file', file)
      const response = await api.post<UploadResponse>(
        `/events/${eventSlug}/messaging/email_blasts/upload_attachment`,
        body
      )
      applyChange([...listRef.current, response.attachment])
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to upload attachment.')
    } finally {
      inFlightBytesRef.current -= claimed
      setUploadCount((count) => count - 1)
    }

    // Always false: antd never uploads on its own, the request above does.
    return false
  }

  const handleRemove = (signedId: string) => {
    applyChange(listRef.current.filter((file) => file.signedId !== signedId))
  }

  // Shown as the files' own sizes, matching the rows above; the limits are
  // checked against the larger encoded size.
  const attachedBytes = value.reduce((total, file) => total + file.byteSize, 0)
  const uploading = uploadCount > 0

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-2 space-y-1">
          {value.map((file) => (
            <div
              key={file.signedId}
              className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1"
            >
              <PaperClipOutlined className="text-gray-400" />
              <span className="flex-1 truncate text-xs" title={file.filename}>
                {file.filename}
              </span>
              <span className="text-xs text-gray-400">{formatFileSize(file.byteSize)}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${file.filename}`}
                  className="text-gray-400 hover:text-red-500"
                  onClick={() => handleRemove(file.signedId)}
                >
                  <HiOutlineTrash />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Upload showUploadList={false} multiple beforeUpload={handleUpload} disabled={disabled || uploading}>
        <Button variant="secondary" icon={<UploadOutlined />} loading={uploading} disabled={disabled}>
          Add attachment
        </Button>
      </Upload>

      <div className="mt-1 text-xs text-gray-400">
        Any file type, up to {formatFileSize(limits.maxFileBytes)} each and{' '}
        {formatFileSize(limits.maxTotalBytes)} per email.
        {value.length > 0 && ` Currently ${formatFileSize(attachedBytes)} attached.`}
      </div>

      <div className="mt-1 flex items-start gap-1.5 text-xs text-gray-400">
        <HiOutlineDocument className="mt-0.5 shrink-0" />
        <span>
          Emails with file attachments have a higher chance of being filtered into the recipient's
          Spam/Junk folder. If the recipient does not receive the email, kindly ask them to check
          their Spam/Junk folder.
        </span>
      </div>
    </div>
  )
}

export default EmailAttachmentsField
